import styled, { keyframes, css } from 'styled-components';

const exhaustPuff = keyframes`
  0% {
    opacity: 0.92;
    transform: translate(0, 0) scale(0.5);
  }
  35% {
    opacity: 0.65;
  }
  100% {
    opacity: 0;
    transform: translate(-18px, -22px) scale(1.65);
  }
`;

export const PER_ROW = 3;

function dotColor(theme, colorToken) {
    const map = {
        gray: theme.colors.gray?.[500] || '#6B7280',
        amber: theme.colors.warning?.main || '#D97706',
        indigo: theme.colors.indigo?.main || '#6366F1',
        blue: theme.colors.info?.main || '#2563EB',
        teal: theme.colors.teal?.main || '#14B8A6',
        orange: theme.colors.orange?.main || '#F97316',
        green: theme.colors.success?.main || '#16A34A',
        red: theme.colors.danger?.main || '#DC2626',
    };
    return map[colorToken] || map.gray;
}

export const TimelineSection = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  padding: clamp(16px, 3vw, 24px);
  margin-bottom: 24px;
  overflow: visible;
`;

export const TimelineTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gray[900]};
  margin: 0 0 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray[100]};
`;

/** Título da seção + indicador opcional (ex.: atraso), sem alterar o layout abaixo. */
export const TimelineTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin: 0 0 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray[100]};

  ${TimelineTitle} {
    margin: 0;
    padding-bottom: 0;
    border-bottom: none;
  }
`;

export const TrackHint = styled.p`
  margin: 0 0 12px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.gray?.[500] || '#6B7280'};
  line-height: 1.45;

  @media (max-width: 640px) {
    font-size: 13px;
  }
`;

/** Contêiner principal: largura total, sem scroll horizontal */
export const TrackWrap = styled.div`
  width: 100%;
  overflow: visible;
`;

export const TrackRowsStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 18px;
  overflow: visible;
`;

/** Uma pista independente por linha (até 3 eventos) */
export const TrackRow = styled.div`
  position: relative;
  --row-pad-x: 6px;
  /* Faixa da “pista”: caminhão e ícone no último ponto usam os mesmos valores */
  --road-bottom: 22px;
  --road-height: 24px;
  padding: 0 var(--row-pad-x);
  /* Altura mínima: legenda + ponto + respiro acima da faixa da pista */
  min-height: calc(var(--road-bottom) + var(--road-height) + 52px);
  overflow: visible;
`;

export const TrackRoadStrip = styled.div`
  position: absolute;
  left: var(--row-pad-x);
  right: var(--row-pad-x);
  bottom: var(--road-bottom);
  height: var(--road-height);
  border-radius: 5px;
  background: linear-gradient(
    180deg,
    ${({ theme }) => theme.colors.gray?.[200] || '#e5e7eb'} 0%,
    ${({ theme }) => theme.colors.gray?.[300] || '#d1d5db'} 100%
  );
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.75), 0 1px 2px rgba(15, 23, 42, 0.05);
  pointer-events: none;
`;

export const TrackLaneStrip = styled.div`
  position: absolute;
  left: var(--row-pad-x);
  right: var(--row-pad-x);
  bottom: calc(var(--road-bottom) + var(--road-height) / 2 - 1px);
  height: 0;
  border-top: 2px dashed rgba(255, 255, 255, 0.65);
  pointer-events: none;
`;

export const TrackRowGrid = styled.div`
  position: relative;
  z-index: 3;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  column-gap: 6px;
  align-items: end;
  /*
   * Reserva vertical até o TOPO da pista cinza: a base útil da célula alinha com a borda superior da faixa,
   * eliminando o vão entre texto e pista (antes o grid só tinha 6px e o conteúdo ia parar no topo da célula).
   */
  --track-grid-pad-bottom: calc(var(--road-bottom) + var(--road-height));
  padding-bottom: var(--track-grid-pad-bottom);
  overflow: visible;
`;

export const VehicleLayerRow = styled.div`
  position: absolute;
  left: var(--row-pad-x);
  right: var(--row-pad-x);
  bottom: var(--road-bottom);
  height: var(--road-height);
  overflow: visible;
  display: flex;
  align-items: flex-end;
  justify-content: flex-start;
  pointer-events: none;
  z-index: 2;
  opacity: ${({ $visible }) => ($visible === false ? 0 : 1)};
  visibility: ${({ $visible }) => ($visible === false ? 'hidden' : 'visible')};
  transition: opacity 0.2s ease;
`;

/** Partículas atrás do caminhão (só enquanto se move) */
export const ExhaustSmoke = styled.div`
  position: absolute;
  right: 100%;
  bottom: 0;
  width: 28px;
  height: 28px;
  pointer-events: none;
  z-index: 0;
  margin-right: -4px;
  overflow: visible;

  span {
    position: absolute;
    left: 0;
    bottom: 2px;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    box-shadow: 0 0 6px rgba(80, 88, 102, 0.45);
    background: radial-gradient(
      circle at 35% 40%,
      rgba(70, 75, 88, 0.88) 0%,
      rgba(120, 125, 138, 0.55) 35%,
      rgba(180, 185, 195, 0.22) 58%,
      transparent 78%
    );
    filter: blur(0.35px);
    ${({ $active }) =>
        $active
            ? css`
                  animation: ${exhaustPuff} 5.4s ease-out infinite;
              `
            : css`
                  animation: none;
                  opacity: 0;
              `}
  }

  span:nth-child(2) {
    left: 5px;
    bottom: 5px;
    width: 12px;
    height: 12px;
    animation-delay: 1.4s;
  }

  span:nth-child(3) {
    left: -2px;
    bottom: 8px;
    width: 13px;
    height: 13px;
    animation-delay: 2.72s;
  }

  @media (prefers-reduced-motion: reduce) {
    display: none;
  }
`;

export const VehicleMover = styled.div`
  position: absolute;
  bottom: 0;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary?.main || '#2563eb'};
  filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.22));
  z-index: 1;
  /* Centro do ícone no eixo do ponto (+ pequeno $aheadPx); base encostada na pista */
  left: calc(${({ $leftPct }) => $leftPct}% + ${({ $aheadPx }) => $aheadPx ?? 0}px);
  transform: translateX(-50%);
  transition: ${({ $motion }) => ($motion ? 'left 7.8s cubic-bezier(0.22, 1, 0.36, 1)' : 'none')};

  @media (prefers-reduced-motion: reduce) {
    transition: none !important;
  }

  svg {
    width: 22px;
    height: 22px;
  }
`;

/**
 * Último ponto (várias linhas): a base da célula = topo da faixa da pista (padding do grid).
 * A base da PISTA fica road-height px abaixo disso → bottom negativo para a base do ícone coincidir com a base cinza.
 */
export const VehicleOnWaypoint = styled.span`
  position: absolute;
  left: 50%;
  bottom: calc(0px - var(--road-height));
  transform: translateX(-50%);
  width: 26px;
  height: 26px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary?.main || '#2563eb'};
  filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.22));
  pointer-events: none;
  z-index: 1;

  svg {
    width: 26px;
    height: 26px;
  }
`;

export const WaypointCell = styled.div`
  position: relative;
  isolation: isolate;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  min-width: 0;
  min-height: 0;
  overflow: visible;
`;

/** Envolve o botão e o popover para manter hover ao mover o cursor para a caixinha */
export const WaypointInteractiveWrap = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  min-width: 0;
  overflow: visible;
`;

export const WaypointHit = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
  /* Pouco espaço sob o texto: legenda fica logo acima da pista sem vão grande */
  padding: 2px 2px 2px;
  margin: 0;
  width: 100%;
  max-width: 100%;
  border: none;
  background: transparent;
  cursor: pointer;
  font: inherit;
  color: inherit;
  border-radius: 8px;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary?.main || '#2563eb'};
    outline-offset: 1px;
  }

  ${({ $active, theme }) =>
        $active
            ? `background: ${
                  theme.colors.primary?.light
                      ? `${theme.colors.primary.light}22`
                      : 'rgba(37, 99, 235, 0.1)'
              };`
            : ''}
`;

export const WaypointDot = styled.span`
  width: ${({ $isCurrent }) => ($isCurrent ? '14px' : '11px')};
  height: ${({ $isCurrent }) => ($isCurrent ? '14px' : '11px')};
  border-radius: 50%;
  background-color: ${({ theme, $colorToken }) => dotColor(theme, $colorToken)};
  border: 2px solid #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.18);
  flex-shrink: 0;
  margin-bottom: 1px;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  ${WaypointHit}:hover &,
  ${WaypointHit}:focus-visible & {
    transform: scale(1.1);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.22);
  }

  ${({ $isCurrent }) =>
        $isCurrent
            ? `
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.28), 0 1px 3px rgba(0, 0, 0, 0.18);
  `
            : ''}
`;

export const WaypointCaption = styled.span`
  font-size: 9px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gray?.[700] || '#374151'};
  text-align: center;
  line-height: 1.15;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  padding: 0 1px;

  @media (max-width: 640px) {
    font-size: 10px;
    line-height: 1.2;
    -webkit-line-clamp: 3;
  }
`;

/** Fundo tocável para fechar o painel de detalhes no mobile */
export const TimelineDetailBackdrop = styled.button`
  position: fixed;
  inset: 0;
  z-index: 1098;
  margin: 0;
  padding: 0;
  border: none;
  background: rgba(15, 23, 42, 0.45);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
`;

/** Caixinha informativa junto ao ponto (desktop: hover / clique) */
export const InfoPopover = styled.div`
  position: absolute;
  bottom: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  width: max-content;
  max-width: min(300px, calc(100vw - 32px));
  padding: 10px 12px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12), 0 0 0 1px ${({ theme }) => theme.colors.gray?.[100] || '#f3f4f6'};
  z-index: 30;
  pointer-events: auto;
  text-align: left;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -6px;
    border: 6px solid transparent;
    border-top-color: #fff;
  }
`;

/**
 * Mesmo conteúdo que InfoPopover, renderizado em document.body no mobile
 * para não ser cortado por overflow/transform de ancestrais.
 */
export const InfoPopoverPortal = styled.div`
  position: fixed;
  left: 12px;
  right: 12px;
  bottom: max(12px, env(safe-area-inset-bottom, 0px));
  top: auto;
  width: auto;
  max-height: min(52vh, 360px);
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  padding: 14px 16px;
  background: #fff;
  border-radius: 12px;
  z-index: 1099;
  pointer-events: auto;
  text-align: left;
  box-shadow:
    0 -8px 32px rgba(0, 0, 0, 0.18),
    0 0 0 1px ${({ theme }) => theme.colors.gray?.[200] || '#e5e7eb'};
`;

export const DetailHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 6px;
  margin-bottom: 6px;
`;

export const DetailTime = styled.time`
  display: block;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.gray?.[500] || '#6B7280'};
  margin-bottom: 4px;

  @media (max-width: 640px) {
    font-size: 13px;
    margin-bottom: 6px;
  }
`;

export const DetailDescription = styled.p`
  margin: 0 0 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.gray?.[900] || '#111827'};
  line-height: 1.45;

  @media (max-width: 640px) {
    font-size: 15px;
    line-height: 1.5;
    margin-bottom: 8px;
  }
`;

export const DetailUser = styled.span`
  display: block;
  font-size: 10px;
  color: ${({ theme }) => theme.colors.gray?.[500] || '#6B7280'};
  font-style: italic;

  @media (max-width: 640px) {
    font-size: 12px;
    line-height: 1.4;
  }
`;

export const DetailMetaBlock = styled.div`
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid ${({ theme }) => theme.colors.gray?.[100] || '#f3f4f6'};
`;

export const DetailMetaLabel = styled.span`
  display: block;
  font-size: 10px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gray?.[500] || '#6B7280'};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 2px;

  @media (max-width: 640px) {
    font-size: 11px;
    margin-bottom: 4px;
  }
`;

export const DetailMetaText = styled.p`
  margin: 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.gray?.[900] || '#111827'};
  line-height: 1.45;

  @media (max-width: 640px) {
    font-size: 14px;
    line-height: 1.5;
  }
`;

export const DetailMetaHint = styled.span`
  display: block;
  margin-top: 2px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.gray?.[500] || '#6B7280'};
  line-height: 1.4;

  @media (max-width: 640px) {
    font-size: 12px;
  }
`;

export const DetailDriverLink = styled.button`
  display: inline;
  margin: 0;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.primary?.main || '#2563EB'};
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
  text-align: left;

  &:hover {
    color: ${({ theme }) => theme.colors.primary?.dark || '#1D4ED8'};
  }

  @media (max-width: 640px) {
    font-size: 14px;
  }
`;

export const PhotoGallerySection = styled.div`
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid ${({ theme }) => theme.colors.gray?.[100] || '#f3f4f6'};
`;

export const PhotoGalleryLabel = styled.span`
  display: block;
  font-size: 10px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gray?.[500] || '#6B7280'};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 6px;

  @media (max-width: 640px) {
    font-size: 11px;
    margin-bottom: 8px;
  }
`;

export const PhotoGalleryGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

export const PhotoGalleryThumbWrapper = styled.div`
  position: relative;
  width: 52px;
  height: 52px;
  flex-shrink: 0;
  overflow: visible;

  @media (max-width: 640px) {
    width: 64px;
    height: 64px;
  }
`;

export const PhotoThumbBtn = styled.button`
  width: 100%;
  height: 100%;
  padding: 0;
  margin: 0;
  border: 2px solid transparent;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  background: ${({ theme }) => theme.colors.gray?.[100] || '#f3f4f6'};
  flex-shrink: 0;
  transition: border-color 0.15s ease, transform 0.15s ease;

  &:hover,
  &:focus-visible {
    border-color: ${({ theme }) => theme.colors.primary?.main || '#2563eb'};
    transform: scale(1.05);
    outline: none;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

export const PhotoLightboxBackdrop = styled.button`
  position: fixed;
  inset: 0;
  z-index: 1200;
  background: rgba(0, 0, 0, 0.88);
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: zoom-out;
  padding: 16px;
  margin: 0;
`;

export const PhotoLightboxImg = styled.img`
  max-width: min(90vw, 900px);
  max-height: min(85vh, 700px);
  object-fit: contain;
  border-radius: 6px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.5);
  pointer-events: none;
  user-select: none;
`;
